import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

//Components
import { Button, Tooltip } from "@gpstrackit/react-controls";
import { Icon } from "@mui/material";
import { TagsGroup } from 'Components/Tag';

//EndPoints
import { endpoints } from 'Core/defaultValues';

//Views
import ChipStatus from 'Components/ChipStatus';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import ReprocessOdoHours from 'Modules/accounts/.components/reprocessOdoHours';
import EdgeTensor from 'Modules/accounts/.components/tabEdgeTensor';
import CreateEditAccount from 'Modules/accounts/views/components/createEditAccount';
import PurgeAccount from 'Modules/accounts/views/components/purgeAccount';
import SyncEntities from 'Modules/accounts/views/components/syncEntities';
import AccountDetails from 'Modules/accounts/views/main/.components/details';
import Activities from "Modules/activities/.components/tab";

//Helpers
import { getTagColor } from 'Core/data/Helpers';

//Security
import { HasPermission } from "Core/security";

//Notification
import { NotificationManager } from "Components/Notifications";

//Actions
import { useTokenOneTimeHandler } from 'Core/hooks';
import {
    accountsCreateEditAccountOpenModal,
    accountsGetDetailsAccountOpenModal,
    activitiesSearchOpenModal,
    openModalEdgeTensor,
    openModalSyncEntities,
    reprocessOdoHoursOpenModal,
    searchEntityRequest
} from 'Redux/actions';

const AccountsTab = (props) => {

    const { dealerId, tab, height, paramDealer } = props;

    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const purgeAccountOModal = useSelector(state => state.purgeAccountRedux.openModal);
    const createEditAccountOModal = useSelector(state => state.createEditAccountRedux.openModal);
    const detailsOpenModal = useSelector(state => state.accountDetailsRedux.openModal);
    const openModalReprocessOdoHours = useSelector(state => state.reprocessOdoHoursRedux.openModal);
    const events = useSelector(state => state.generalRedux.events);
    const user = useSelector(state => state.securityRedux.user);
    const dealers = useSelector(state => state.generalRedux.dealers);
    const openModalActivities = useSelector(state => state.activitiesTabRedux.openModal);
    const openModalSyncZonar = useSelector(state => state.syncEntitiesRedux.openModal);
    const edgeTensorOpenModal = useSelector(state => state.edgeTensorRedux.openModal);
    const tokenOneTime = useSelector(state => state.accountOverviewRedux.tokenOneTime);
    const { handleAuthenticate } = useTokenOneTimeHandler(tokenOneTime, dealers);
    const statusOptions = [
        {
            id: 1,
            label: messages['active']
        },
        {
            id: 4,
            label: messages['cancelled']
        },
        {
            id: 3,
            label: messages['involuntary_suspension']
        },
        {
            id: 2,
            label: messages['voluntary_suspension']
        }
    ]

    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(accountsGetDetailsAccountOpenModal(true, item.id))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "id",
            title: messages['accounts_tab_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "accounts_tab_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['accounts_tab_name'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_tab_name",
            render: (item) => {
                let name = item?.name
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/accounts/overview/${item?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            },
            export: true,
            functionString: 'return row?.name'
        },
        {
            key: "dealer_name",
            field: "dealer.name",
            title: messages['accounts_tab_dealer'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_tab_dealer",
            render: (item) => {
                return <span>{item?.dealer?.name}</span>
            },
            export: true,
            functionString: 'return row?.dealer?.name'
        },
        {
            key: "dealer_brand_title",
            field: "dealer { id brandId brand { id title } }",
            title: messages['accounts_tab_brand'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_tab_brand",
            render: (item) => {
                return <span>{item?.dealer?.brand?.title}</span>
            },
            export: true,
            functionString: 'return row?.dealer?.brand?.title'
        },
        {
            key: "role_name",
            field: "role { id name }",
            title: messages['accounts_tab_package'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_tab_package",
            render: (item) => {
                return <span>{item?.role?.name}</span>
            },
            export: true,
            functionString: 'return row?.role?.name'
        },
        {
            key: "crmId",
            field: "crmId",
            title: messages['accounts_tab_crm'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_tab_crm",
            render: (item) => {
                return <a
                    style={{ color: 'black' }}
                    href={`${endpoints.SALESFORCE_URL}lightning/r/Account/${item?.crmId}/view`}
                    target="_blank"
                >
                    {item?.crmId}
                </a>
            },
            export: true,
            functionString: 'return row?.crmId'
        },
        {
            key: "devicesCount",
            field: "devicesCount",
            title: messages['account_overview_active_devices'],
            width: 200,
            show: true,
            sort: true, //-- THROWS ERROR: CustomException
            locked: false,
            resize: true,
            code: "account_overview_active_devices",
            render: (item) => {
                return <span>{item?.devicesCount}</span>
            },
            export: true,
            functionString: 'return row?.devicesCount'
        },
        {
            key: "status",
            field: "status",
            title: messages['accounts_tab_status'],
            sort: true,
            width: 200,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_tab_status",
            render: (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        message = `${messages['accounts_tab_next_scheduled_action']}:`
                        if (data && data?.action === "statusUpdate") {
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            if (data?.entityName === "Account") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_tab_at']} ${data?.scheduleOn}`
                            } else if (data?.entityName === "Unit") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_tab_on']} ${messages['accounts_tab_devices']} ${messages['accounts_tab_at']} ${data?.scheduleOn}`
                            }
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            },
            export: true,
            functionString: 'return `${row?.status === 1 ? "Active" : row?.status === 2 ?  "Voluntary Suspension" : row?.status === 3 ?  "Involuntary Suspension" : row?.status === 4 ?  "Cancelled" : row?.status === 6 ?  "RMA" : "Desactivated" }`;'
        },
        {
            key: "tags",
            field: "tags ",
            title: messages['accounts_main_tags'],
            width: 350,
            show: true,
            locked: false,
            resize: true,
            code: "accounts_main_tags",
            render: (item) => {
                let unitTags = [];
                let items = item?.tags ? JSON.parse(item?.tags) : [];
                if (items) {
                    unitTags = items?.map(t => {
                        return { ...t, color: getTagColor(t?.color) };
                    });
                }

                unitTags = unitTags?.filter(unitTag => unitTag?.id != -1);

                return (
                    <TagsGroup
                        options={unitTags}
                        nameField="label"
                        colorField="color"
                    />
                )
            },
            export: true,
            functionString: 'return row?.tags?.map(x => x.label).join(",\r\n")'
        },
        {
            key: "registrationStatus",
            field: "registrationStatus",
            title: messages['account_overview_registration_status'],
            width: 200,
            show: true,
            sort: false,
            locked: false,
            resize: true,
            code: "account_overview_registration_status",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        <ChipStatus label={item?.registrationStatus} messages={messages} />
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.registrationStatus'
        },
        {
            key: "readonly",
            field: "readonly",
            title: messages['accounts_main_readonly'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            code: "accounts_main_readonly",
            render: (item) => {
                return (
                    <div style={{ width: 210, height: 25 }}>
                        {item?.readonly ? messages['common_yes'] : messages['common_no']}
                    </div>
                )
            },
            export: true,
            functionString: 'return row?.readonly ? "Yes" : "No"'
        }
    ])

    const [currentSort, setCurrentSort] = useState([{ field: "id", order: "ASC" }]);
    const LIMIT = 50;

    const [searchFields, setSearchFields] = useState(["name", "username", "email", "dealer_name"]);

    const tagsHandler = (newTags) => {
        setTags(newTags?.terms || []);
    }

    useEffect(() => {
        (!events || events.length === 0) && dispatch(searchEntityRequest({ key: "events" }));
        (!dealers || dealers.length === 0) && dispatch(searchEntityRequest({ key: "dealers" }));
    }, []);

    let innerHeight = window.innerHeight

    // ============================================
    // DataViewInfiniteScroll Callback Handlers
    // ============================================

    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort) => {
        let currentConditions = {}
        let currentTags = {}

        if (!conditions && tags) {
            currentConditions = tableRef?.current?.conditions;
            currentTags = tags;
        } else {
            currentConditions = conditions || [];
            currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
            if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
        }

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'dealerId');

        let tagsById = currentTags.filter((item) => typeof item.id === "number");
        if (tagsById?.length > 0) {
            let currentIds = tagsById.map((item) => (item.id));
            if (currentIds.length > 0) {
                currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
            }
        }

        let tagsByLabel = currentTags.filter((item) => typeof item.id === "string");

        if (tagsByLabel.length > 0) {
            let currentLabels = tagsByLabel.map((item) => (item.name))
            if (currentLabels.length > 0) {
                currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
            }
        }

        currentConditions.push({ fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" });

        tableRef?.current?.getData({ newSort: sort, newConditions: currentConditions, newColumns });
    };

    const handleChangeColumns = (items) => {
        let currentColumns = items.map((item) => ({
            ...item,
            title: item.code ? messages[item.code] : item.title,
            render: item.key === "status" ? (item) => {
                let nextScheduledAction = item?.nextScheduledAction
                let message = null
                if (nextScheduledAction) {
                    try {
                        let data = JSON.parse(nextScheduledAction)
                        message = `${messages['accounts_tab_next_scheduled_action']}:`
                        if (data && data?.action === "statusUpdate") {
                            let status = statusOptions.find(item => item?.id === data?.data?.newStatusId)
                            if (data?.entityName === "Account") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_tab_at']} ${data?.scheduleOn}`
                            } else if (data?.entityName === "Unit") {
                                message = `${messages[data?.action] || data?.action}${status ? ` ${messages[status?.label] || status?.label}` : ''} ${messages['accounts_tab_on']} ${messages['accounts_tab_devices']} ${messages['accounts_tab_at']} ${data?.scheduleOn}`
                            }
                        }
                    } catch (exc) { }
                }
                return (
                    <div style={{ display: 'flex' }}>
                        <ChipStatus statusCode={item?.status} messages={messages} />
                        {nextScheduledAction &&
                            <div style={{ marginLeft: 5 }}>
                                <Tooltip title={message}>
                                    <Icon style={{ color: '#1664f5', cursor: 'pointer' }}>access_alarms</Icon>
                                </Tooltip>
                            </div>
                        }
                    </div>
                )
            } : item.render,
        }))
        setColumns(currentColumns)
    };

    const tableOptions = [
        {
            id: "accounts_tab_authenticate_cloud",
            label: messages['accounts_tab_authenticate_cloud'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "cmplc"),
            action: (items, event) => {
                let currentItem = items.find(item => item.id);
                handleAuthenticate(event, currentItem?.dealer?.id, currentItem?.email || currentItem?.username, `PX_${currentItem?.id}`);
            }
        },
        {
            id: "accounts_tab_edit",
            label: messages['accounts_tab_edit'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "cmp", "u"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(accountsCreateEditAccountOpenModal(currentItem))
            }
        },
        {
            id: "accounts_tab_reprocess_odo_hours",
            label: messages['accounts_tab_reprocess_odo_hours'],
            single: true,
            show: true,
            disabled: !HasPermission(user, "rprcssoh", "c"),
            action: (items) => {
                let currentItem = items.find(item => item.id);
                dispatch(reprocessOdoHoursOpenModal({ selectedAccount: currentItem }))
            }
        }
    ];

    const getMenuOptions = () => {
        return [
            messages['accounts_tab_authenticate_cloud'],
            messages['accounts_tab_edit'],
            messages['accounts_tab_reprocess_odo_hours'],
            messages['accounts_main_syncZonar'],
            messages['accounts_main_edge_tensor']
        ]
    };

    const handleClickMenu = (event, item, clickEvent) => {
        if (event === messages['accounts_tab_authenticate_cloud']) {
            if (HasPermission(user, "cmplc")) {
                handleAuthenticate(clickEvent, item?.dealer?.id, item?.email || item?.username, `PX_${item?.id}`);
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_tab_authenticate_cloud'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_tab_edit']) {
            if (HasPermission(user, "cmp", "u")) {
                dispatch(accountsCreateEditAccountOpenModal(item))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_tab_edit'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_tab_reprocess_odo_hours']) {
            if (HasPermission(user, "rprcssoh", "c")) {
                dispatch(reprocessOdoHoursOpenModal({ selectedAccount: item }))
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_tab_edit'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_syncZonar']) {
            if (HasPermission(user, "synczon", "u")) {
                if (item?.integrations?.length) {
                    let zonar = item?.integrations?.some(integration => integration?.integration?.code === "zonar");
                    if (zonar) {
                        dispatch(openModalSyncEntities({ account: item }))
                    } else {
                        NotificationManager.warning(
                            messages['accounts_main_error_integration_zonar'],
                            messages['accounts_main_syncZonar'],
                            5000,
                        );
                    }
                } else {
                    NotificationManager.warning(
                        messages['accounts_main_error_integration_zonar'],
                        messages['accounts_main_syncZonar'],
                        5000,
                    );
                }
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_syncZonar'],
                    5000,
                );
            }
        }
        if (event === messages['accounts_main_edge_tensor']) {
            if (HasPermission(user, "edgtnsr")) {
                dispatch(openModalEdgeTensor({ open: true, account: item }));
            } else {
                NotificationManager.warning(
                    messages['common_error_permission'],
                    messages['accounts_main_edge_tensor'],
                    5000,
                );
            }
        }
    };

    const renderExtraButton = () => {
        return (
            <div style={{ margin: '0 5px' }}>
                <Button
                    disabled={!HasPermission(user, "cmp", "c")}
                    className="btn-dark-blue"
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(accountsCreateEditAccountOpenModal())
                    }}
                >
                    {messages['accounts_tab_add_account']}
                </Button>
            </div>
        )
    };

    const customColumnConfig = {
        render: (row) => {
            return (
                <div>
                    <Tooltip title={messages['users_tab_show_activities']}>
                        <Icon style={{ cursor: "pointer" }} className="toolbar-list-icon fs-20" onClick={() => {
                            if (HasPermission(user, "act")) {
                                dispatch(activitiesSearchOpenModal({ entityName: "Company", entityId: row.id }, "accounts-tab-modal", row.id))
                            } else {
                                NotificationManager.warning(
                                    messages['common_error_permission'],
                                    messages['users_tab_users'],
                                    5000,
                                );
                            }
                        }} >
                            assignment
                        </Icon>
                    </Tooltip>
                </div>
            )
        }
    };

    return (
        <div className="accounts-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                key="accounts-tab"
                ref={tableRef}
                name="pm-accounts-tab"
                columns={columns}
                infiniteScroll
                entity="accounts-tab"
                graphqlConfig={{
                    function: "searchCompanies",
                    sortType: "CompanySortInput",
                    conditionsType: "CompanySearchInput",
                    dynamicQuery: true,
                    customQuery: "id sm nextScheduledAction username email dealer { id }",
                }}
                searchConfig={{
                    conditions: ["name", "username", "email"],
                    optionValue: "id",
                    optionLabel: "name",
                }}
                exportConfig={{
                    fileName: "accounts-tab",
                }}
                uiConfig={{
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                    hideSplit: true,
                }}
                defaultsConfig={{
                    sort: currentSort,
                    rowsPerPage: LIMIT,
                }}
                customSearch={{ fields: ["dealerId"], terms: [dealerId], exact: true, conditionOperator: "AND" }}
                onChangeConditions={handleChangeConditions}
                onChangeColumns={handleChangeColumns}
                tableButtons={["export", 'orderColumn']}
                options={tableOptions}
                menuOptions={getMenuOptions}
                onClickMenu={handleClickMenu}
                extraButton={renderExtraButton}
                customColumn={customColumnConfig}
            />
            {purgeAccountOModal && <PurgeAccount />}
            {createEditAccountOModal && <CreateEditAccount tableRef={tableRef} paramDealer={paramDealer} />}
            {detailsOpenModal && <AccountDetails />}
            {openModalReprocessOdoHours && <ReprocessOdoHours />}
            {openModalActivities && <Activities noFilters noOpenSearch modalView noUsers height={innerHeight - 257} entity={"activitiesTabAccounts"} instanceId="accounts-tab-modal" />}
            {openModalSyncZonar && <SyncEntities />}
            {edgeTensorOpenModal && <EdgeTensor height={innerHeight - 257} mode="modal" />}
        </div>
    )
}
export default AccountsTab;
